import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ToggleGroup, ToggleGroupCount, ToggleGroupItem } from "@/components/ui/toggle-group";
import { DataTable, Icon, KpiCard, KpiGrid, TextoTruncado, Tip } from "@/components/ds";
import { CeldaDocumentosRecurso } from "@/components/recurso-documentos/CeldaDocumentosRecurso";
import {
  FormDrawer,
  FormDrawerContent,
  FormDrawerDescription,
  FormDrawerFooter,
  FormDrawerFooterHint,
  FormDrawerHeader,
  FormDrawerTitle,
  FormDrawerClose,
  formDrawerBodyClass,
} from "@/components/ds/FormDrawer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertCircle, Loader2, Plus, Search } from "@/components/ds/icons";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import { formatNombreArea, ordenarAreas } from "@/lib/area";
import { estadoDocumental } from "@/lib/recurso-documentos-estado";
import { VehiculoForm } from "@/pages/recursos/VehiculoForm";
import type { Vehiculo, VehiculosResponse } from "@/types/vehiculo";
import { PageHeader } from "@/components/ui/PageHeader";
import type { DriveStep } from "driver.js";
import { useGuide } from "@/hooks/useGuide";
import { useRegisterPageGuide } from "@/contexts/ActiveGuideContext";
import { useGuideAutostart } from "@/hooks/useGuideAutostart";
import { usePendingGuide } from "@/hooks/usePendingGuide";
import { useSetupStatusContext } from "@/contexts/SetupStatusContext";

// ─── Vocabulario ─────────────────────────────────────────────────────────────

/**
 * Las cuatro vistas del listado. `papelera` NO es un estado más del vehículo:
 * es un conjunto aparte que se le pide al servidor con otro filtro, por eso
 * viaja como vista y no como un valor del filtro de estado.
 *
 * `todos` es activos + inactivos, el mismo padrón sobre el que se calculan las
 * tarjetas — así el número del riel y el de la tarjeta "Total en inventario"
 * no pueden discrepar.
 */
type Vista = "todos" | "activos" | "inactivos" | "papelera";

function plural(n: number, singular: string, plural: string): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

// El criterio documental (qué es "al día", qué tono lleva la nota) vive en
// `lib/recurso-documentos-estado` y la celda en `CeldaDocumentosRecurso`: las
// tres pantallas de equipos —EPP, Vehículos y Maquinaria— comparten diseño y
// contrato de API, así que escribirlo por pantalla es exactamente cómo dos de
// ellas terminan midiendo distinto.

// ─── Celdas ──────────────────────────────────────────────────────────────────

function CeldaAreas({ v }: { v: Vehiculo }) {
  const areas = ordenarAreas(v.areas ?? []);

  // Las áreas son un clasificador OPCIONAL: un vehículo sin área es normal y
  // se muestra en gris, nunca como un error.
  if (areas.length === 0) return <span className="text-soft text-xs">Sin área</span>;

  return (
    <TextoTruncado className="text-muted-foreground block truncate pr-2 text-xs">
      {areas.map(formatNombreArea).join(" · ")}
    </TextoTruncado>
  );
}

function EstadoBadge({ v }: { v: Vehiculo }) {
  if (v.eliminado_at) {
    return (
      <Badge variant="muted" dot>
        En papelera
      </Badge>
    );
  }
  return (
    <Badge variant={v.activo ? "success" : "neutral"} dot>
      {v.activo ? "Activo" : "Inactivo"}
    </Badge>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export function Vehiculos() {
  const [, navigate] = useLocation();

  /**
   * Todo lo que NO está en la papelera: activos + inactivos. Es el conjunto
   * sobre el que se calculan las cuatro tarjetas, para que numerador y
   * denominador salgan siempre del mismo padrón.
   */
  const [inventario, setInventario] = useState<Vehiculo[]>([]);
  const [papelera, setPapelera] = useState<Vehiculo[]>([]);
  const [tiposTotal, setTiposTotal] = useState(0);

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [vista, setVista] = useState<Vista>("activos");
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  // Mover a papelera (DELETE → soft delete)
  const [aEliminar, setAEliminar] = useState<Vehiculo | null>(null);
  const [eliminando, setEliminando] = useState(false);

  // Restaurar desde papelera
  const [aRestaurar, setARestaurar] = useState<Vehiculo | null>(null);
  const [restaurando, setRestaurando] = useState(false);

  // Purgar (hard delete)
  const [aPurgar, setAPurgar] = useState<Vehiculo | null>(null);
  const [purgando, setPurgando] = useState(false);
  const [confirmPurgarTexto, setConfirmPurgarTexto] = useState("");

  // --- Guía de Vehículos ---
  const { invalidate } = useSetupStatusContext();

  /**
   * guideInDialog: true mientras la guía está activa con el diálogo abierto.
   * Se usa para pasar modal={false} al FormDrawer durante esos pasos,
   * evitando que el focus trap de Radix bloquee los botones Siguiente/Anterior
   * del popover de driver.js (que se renderizan fuera del DOM del diálogo).
   *
   * En el flujo normal (sin guía) el diálogo sigue siendo modal={true} por defecto.
   */
  const [guideInDialog, setGuideInDialog] = useState(false);

  /**
   * Ref para cancelar el rAF de polling del primer campo del diálogo.
   * Se necesita para no tener efectos secundarios si el componente desmonta.
   */
  const dialogPollRafRef = useRef<number>(0);

  /**
   * Fábrica de pasos lazy: se invoca en startGuide() para capturar el estado
   * vivo de setCreateOpen y setGuideInDialog (estables entre renders).
   *
   * Paso 2 — Highlight del botón Nuevo vehículo:
   *   - onHighlightStarted: agrega driver-fc-dialog-step al body → driver z-index
   *     baja a 40/45 (bajo Radix z-50), el botón queda resaltado sin tapar nada.
   *   - onDeselected: remueve driver-fc-dialog-step → driver vuelve a su z default (~10000).
   *   - onNextClick: abre el diálogo, activa modal={false} en el dialog, hace poll
   *     rAF para esperar que monte el primer campo antes de avanzar.
   *
   * Pasos 3–5 — Campos dentro del diálogo:
   *   - driver en z default (~10000), por encima del Radix Dialog (z-50).
   *   - driver-fc-dialog-step NO está en el body → overlay de driver es visible.
   *   - Radix Dialog tiene modal={false} → focus trap desactivado →
   *     botones Next/Prev de driver son clickeables.
   *
   * Paso 6 — Botón Guardar (dentro del diálogo):
   *   - onNextClick: desactiva modal={false}, avanza al popover de cierre.
   *
   * Paso 7 — Popover de cierre:
   *   - Desactiva modal={false}, el diálogo puede cerrarse normalmente.
   */
  const buildVehiculosGuideSteps = useCallback((): DriveStep[] => [
    // Paso 1 — Popover centrado de apertura (sin elemento anclado)
    {
      popover: {
        title: "¿Para qué registrar vehículos?",
        description:
          "Acá registras los vehículos que prestan servicio en tu empresa — por ejemplo, camionetas en obra. Sin vehículos registrados no podrás realizar los checks de vehículos ni llevar el control de tu flota. Es un paso opcional, pero necesario si operas con vehículos. Creemos el primero.",
      },
    },
    // Paso 2 — Highlight del botón Nuevo vehículo (pre-diálogo)
    // driver-fc-dialog-step body class está activa → driver z-index bajo (40/45) bajo Radix
    {
      element: '[data-tour="vehiculos-crear"]',
      onHighlightStarted: () => {
        document.body.classList.add("driver-fc-dialog-step");
      },
      onDeselected: () => {
        // Removemos la clase antes de los pasos in-dialog para que driver
        // vuelva a su z default (~10000) y pueda resaltar los campos dentro del diálogo.
        document.body.classList.remove("driver-fc-dialog-step");
      },
      popover: {
        title: "Agregar un vehículo",
        description:
          "Acá agregas un vehículo al inventario de tu empresa. Haz clic en «Siguiente» para abrir el formulario.",
        onNextClick: (_el, _step, { driver: driverObj }) => {
          // Abre el diálogo y desactiva el focus trap para que driver mantenga
          // el control. El poll rAF espera a que monte el primer campo antes
          // de avanzar, evitando que driver intente resaltar un nodo inexistente.
          setCreateOpen(true);
          setGuideInDialog(true);

          cancelAnimationFrame(dialogPollRafRef.current);
          const deadline = performance.now() + 3000;
          const tick = () => {
            if (document.querySelector('[data-tour="vehiculos-patente"]')) {
              driverObj.moveNext();
              return;
            }
            if (performance.now() >= deadline) {
              // Fallback: avanzamos igual para no dejar la guía atascada.
              driverObj.moveNext();
              return;
            }
            dialogPollRafRef.current = requestAnimationFrame(tick);
          };
          dialogPollRafRef.current = requestAnimationFrame(tick);
        },
      },
    },
    // Paso 3 — Campo Patente (dentro del diálogo, primer campo visible)
    {
      element: '[data-tour="vehiculos-patente"]',
      popover: {
        title: "Patente del vehículo",
        description:
          "Ingresa la patente tal como aparece en el permiso de circulación, por ejemplo «AB-1234». Es el identificador único del vehículo en el sistema.",
      },
    },
    // Paso 4 — Campos Tipo / Marca / Modelo (dentro del diálogo)
    {
      element: '[data-tour="vehiculos-tipo-marca-modelo"]',
      popover: {
        title: "Tipo, marca y modelo",
        description:
          "Completa el tipo (Camioneta, Camión, Van…), la marca y el modelo. Estos datos aparecen en los checks y en los reportes de flota.",
      },
    },
    // Paso 5 — Botón Guardar (dentro del diálogo)
    {
      element: '[data-tour="vehiculos-guardar"]',
      popover: {
        title: "Guardar vehículo",
        description:
          "Cuando termines de completar los datos, guarda para registrar el vehículo en tu flota.",
        onNextClick: (_el, _step, { driver: driverObj }) => {
          // Al salir de los pasos in-dialog, volvemos al comportamiento modal normal.
          setGuideInDialog(false);
          driverObj.moveNext();
        },
      },
    },
    // Paso 6 — Popover de cierre/confirmación (sin elemento anclado)
    {
      popover: {
        title: "¡Listo!",
        description:
          "Puedes reabrir esta guía cuando quieras haciendo clic en «Ver guía» en el panel de primeros pasos, o en el botón de ayuda de esta página.",
      },
    },
  ], [setCreateOpen, setGuideInDialog]);

  const { startGuide } = useGuide("guide-vehiculos", { buildSteps: buildVehiculosGuideSteps });
  useRegisterPageGuide(startGuide);
  usePendingGuide("guide-vehiculos", startGuide, { anchorSelector: '[data-tour="vehiculos-crear"]' });
  useGuideAutostart("guide-vehiculos", startGuide, { anchorSelector: '[data-tour="vehiculos-crear"]' });

  // Limpieza del rAF de polling al desmontar
  useEffect(() => {
    return () => cancelAnimationFrame(dialogPollRafRef.current);
  }, []);
  // --- fin Guía de Vehículos ---

  /**
   * Dos pedidos, no uno por pestaña: el inventario y la papelera son conjuntos
   * distintos del servidor, y las pestañas muestran el conteo de las TRES
   * aunque el usuario esté parado en una sola.
   */
  const cargar = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const [inv, pap] = await Promise.all([
        api.get<VehiculosResponse>("/vehiculos?all=1&include_inactivos=1"),
        api.get<VehiculosResponse>("/vehiculos?all=1&en_papelera=1"),
      ]);
      setInventario(inv.data);
      setPapelera(pap.data);
      setTiposTotal(inv.documentos?.tipos_total ?? 0);
    } catch {
      setLoadError("No se pudo cargar la lista de vehículos.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // ── Mover a papelera (DELETE → soft delete) ───────────────────────────────
  const moverAPapelera = async () => {
    if (!aEliminar) return;
    setEliminando(true);
    try {
      await api.delete(`/vehiculos/${aEliminar.id}`);
      toast.success(
        `Vehículo ${aEliminar.patente} movido a la papelera. Puedes restaurarlo cuando quieras.`,
      );
      setAEliminar(null);
      cargar();
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        toast.error("Este vehículo ya está en la papelera.");
      } else {
        toast.error("No se pudo mover el vehículo a la papelera.");
      }
    } finally {
      setEliminando(false);
    }
  };

  // ── Restaurar desde papelera ──────────────────────────────────────────────
  const restaurar = async () => {
    if (!aRestaurar) return;
    setRestaurando(true);
    try {
      await api.post(`/vehiculos/${aRestaurar.id}/restaurar`, {});
      toast.success(`Vehículo ${aRestaurar.patente} restaurado.`);
      setARestaurar(null);
      cargar();
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        toast.error("Este vehículo no está en la papelera.");
      } else {
        toast.error("No se pudo restaurar el vehículo.");
      }
    } finally {
      setRestaurando(false);
    }
  };

  // ── Purgar (hard delete, irreversible) ────────────────────────────────────
  const purgar = async () => {
    if (!aPurgar) return;
    setPurgando(true);
    try {
      await api.delete(`/vehiculos/${aPurgar.id}/purgar`);
      toast.success(`Vehículo ${aPurgar.patente} eliminado permanentemente.`);
      setAPurgar(null);
      setConfirmPurgarTexto("");
      cargar();
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        toast.error("Solo se puede purgar un vehículo desde la papelera.");
      } else {
        toast.error("No se pudo purgar el vehículo.");
      }
      setAPurgar(null);
      setConfirmPurgarTexto("");
    } finally {
      setPurgando(false);
    }
  };

  // ── Conjuntos derivados ───────────────────────────────────────────────────

  const activos = useMemo(() => inventario.filter((v) => v.activo), [inventario]);
  const inactivos = useMemo(() => inventario.filter((v) => !v.activo), [inventario]);

  /**
   * Las cuatro tarjetas se pliegan del MISMO conjunto (`inventario`) y con la
   * MISMA función que pinta la columna. Un vehículo inactivo con documentos
   * vencidos sigue contando: sale de las asignaciones operativas, no del
   * inventario, y esos papeles hay que arreglarlos antes de reactivarlo.
   */
  const kpis = useMemo(() => {
    const estados = inventario.map((v) => estadoDocumental(v.documentos, tiposTotal));
    return {
      total: inventario.length,
      conVencidos: estados.filter((e) => e && e.vencidos > 0).length,
      conFaltantes: estados.filter((e) => e && e.faltantes > 0).length,
      completos: estados.filter((e) => e?.completo).length,
    };
  }, [inventario, tiposTotal]);

  const filas = useMemo(() => {
    const base =
      vista === "todos"
        ? inventario
        : vista === "activos"
          ? activos
          : vista === "inactivos"
            ? inactivos
            : papelera;
    const q = search.trim().toLowerCase();
    if (!q) return base;
    return base.filter(
      (v) =>
        v.patente.toLowerCase().includes(q) ||
        (v.sigla ?? "").toLowerCase().includes(q) ||
        (v.marca ?? "").toLowerCase().includes(q) ||
        (v.modelo ?? "").toLowerCase().includes(q),
    );
  }, [vista, inventario, activos, inactivos, papelera, search]);

  const esPapelera = vista === "papelera";

  const mensajeVacio = search.trim()
    ? "Ningún vehículo coincide con esa búsqueda."
    : esPapelera
      ? "No hay vehículos en la papelera."
      : vista === "inactivos"
        ? "No hay vehículos inactivos."
        : // Con inventario cargado pero nada activo, el mensaje de bienvenida
          // mentiría: sí hay vehículos, están todos inactivos.
          vista === "activos" && inventario.length > 0
          ? "No hay vehículos activos."
          : 'Todavía no hay vehículos. Haz clic en "Nuevo vehículo" para empezar.';

  return (
    <div className="space-y-6">
      <PageHeader
        title="Vehículos"
        subtitle="Tu flota, con el estado de los documentos obligatorios de cada vehículo."
        rightContent={
          <Button data-tour="vehiculos-crear" onClick={() => setCreateOpen(true)} className="shrink-0">
            <Plus className="h-4 w-4" />
            Nuevo vehículo
          </Button>
        }
      />

      {/*
        modal={false} desactiva el focus trap de Radix Dialog durante los pasos
        in-dialog de la guía. Esto permite que los botones Siguiente/Anterior de
        driver.js (renderizados fuera del DOM del diálogo) sean clickeables.
        En el flujo normal sin guía, guideInDialog=false y el diálogo es modal.
      */}
      <FormDrawer
        open={createOpen}
        onOpenChange={(open) => {
          if (!open) setGuideInDialog(false);
          setCreateOpen(open);
        }}
        side="right"
        modal={guideInDialog ? false : undefined}
        dismissible={guideInDialog ? false : undefined}
      >
        <VehiculoFormDrawer
          key={createOpen ? "create-open" : "create-closed"}
          mode="create"
          onSaved={() => {
            setCreateOpen(false);
            setGuideInDialog(false);
            cargar();
            invalidate();
          }}
        />
      </FormDrawer>

      <KpiGrid min={196}>
        <KpiCard
          label="Total en inventario"
          valor={kpis.total}
          detalle={`${plural(activos.length, "activo", "activos")} · ${plural(
            inactivos.length,
            "inactivo",
            "inactivos",
          )}`}
        />
        <KpiCard
          label="Con documentos vencidos"
          valor={kpis.conVencidos}
          alerta={kpis.conVencidos > 0}
          detalle={`de ${plural(kpis.total, "vehículo", "vehículos")} en el inventario`}
        />
        <KpiCard
          label="Con documentos sin subir"
          valor={kpis.conFaltantes}
          detalle={
            tiposTotal === 0
              ? "sin catálogo de documentos configurado"
              : `les falta al menos 1 de ${plural(tiposTotal, "documento", "documentos")}`
          }
        />
        <KpiCard
          label="Documentación completa"
          valor={`${kpis.completos}/${kpis.total}`}
          detalle="con todo el catálogo cargado y nada vencido"
        />
      </KpiGrid>

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12 pt-6">
            <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
          </CardContent>
        </Card>
      ) : loadError ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 pt-6 text-center">
            <AlertCircle className="text-destructive h-6 w-6" />
            <p className="text-muted-foreground text-sm">{loadError}</p>
            <button type="button" onClick={cargar} className="text-primary-hover text-sm underline">
              Reintentar
            </button>
          </CardContent>
        </Card>
      ) : (
        <DataTable
          rows={filas}
          getRowKey={(v) => v.id}
          // 956px de columnas + 28px del `px-3.5` de la fila. Por debajo de esa
          // suma la grilla se desborda de su contenedor en vez de scrollear, y
          // la cabecera deja de alinearse con las filas.
          minWidth={1000}
          pageSize={20}
          onRowClick={esPapelera ? undefined : (v) => navigate(`/vehiculos/${v.id}/ficha`)}
          emptyMessage={mensajeVacio}
          emptyIcon={esPapelera ? "delete" : "local_shipping"}
          toolbar={
            <>
              <ToggleGroup
                type="single"
                variant="segmented"
                value={vista}
                onValueChange={(v) => {
                  if (!v) return;
                  setVista(v as Vista);
                  setSearch("");
                }}
              >
                <ToggleGroupItem value="todos">
                  Todos
                  <ToggleGroupCount>{inventario.length}</ToggleGroupCount>
                </ToggleGroupItem>
                <ToggleGroupItem value="activos">
                  Activos
                  <ToggleGroupCount>{activos.length}</ToggleGroupCount>
                </ToggleGroupItem>
                <ToggleGroupItem value="inactivos">
                  Inactivos
                  <ToggleGroupCount>{inactivos.length}</ToggleGroupCount>
                </ToggleGroupItem>
                <ToggleGroupItem value="papelera">
                  <Icon name="delete" size={16} />
                  Papelera
                  <ToggleGroupCount>{papelera.length}</ToggleGroupCount>
                </ToggleGroupItem>
              </ToggleGroup>

              <div className="relative min-w-[190px] flex-1">
                <Search className="text-soft absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2" />
                <Input
                  placeholder="Buscar por patente, sigla o marca…"
                  className="h-9 pl-8 text-[13px]"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </>
          }
          columns={[
            {
              key: "patente",
              header: "Patente",
              width: "104px",
              cell: (v) => <span className="fc-num text-[12.5px] font-medium">{v.patente}</span>,
            },
            {
              key: "sigla",
              header: "Sigla",
              width: "72px",
              cell: (v) => <span className="fc-num text-soft text-[11.5px]">{v.sigla ?? "—"}</span>,
            },
            {
              key: "tipo",
              header: "Tipo",
              width: "110px",
              cell: (v) => (
                <TextoTruncado className="block truncate pr-2 text-[12.5px]">
                  {v.tipo ?? "—"}
                </TextoTruncado>
              ),
            },
            {
              key: "marca",
              header: "Marca / modelo",
              width: "minmax(150px,1fr)",
              cell: (v) => (
                <TextoTruncado className="block truncate pr-2 text-[12.5px]">
                  {[v.marca, v.modelo].filter(Boolean).join(" ") || "—"}
                </TextoTruncado>
              ),
            },
            {
              key: "anio",
              header: "Año",
              width: "60px",
              cell: (v) => (
                <span className="fc-num text-muted-foreground text-[11.5px]">{v.anio ?? "—"}</span>
              ),
            },
            {
              key: "areas",
              header: "Área",
              width: "120px",
              cell: (v) => <CeldaAreas v={v} />,
            },
            {
              key: "documentos",
              header: "Documentos",
              width: "168px",
              cell: (v) =>
                // En la papelera la columna no aplica: el vehículo salió del
                // inventario y sus documentos no se están exigiendo.
                esPapelera ? (
                  <span className="text-soft text-xs">—</span>
                ) : (
                  <CeldaDocumentosRecurso resumen={v.documentos} tiposTotal={tiposTotal} />
                ),
            },
            {
              key: "estado",
              header: "Estado",
              width: "100px",
              cell: (v) => <EstadoBadge v={v} />,
            },
            {
              // Sin encabezado, igual que el prototipo. Cadena vacía y NO
              // `srOnlyHeader`: `sr-only` es `position:absolute`, así que sobre
              // una celda de la grilla la saca del flujo y corre las columnas
              // siguientes — ver el comentario en DataTable.
              key: "acciones",
              header: "",
              width: "72px",
              align: "right",
              cell: (v) =>
                esPapelera ? (
                  <div className="flex items-center justify-end gap-0.5">
                    <Tip texto="Devolver al inventario">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Restaurar ${v.patente}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setARestaurar(v);
                        }}
                      >
                        <Icon name="restore_from_trash" size={16} />
                      </Button>
                    </Tip>
                    <Tip texto="Eliminar definitivamente">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-soft hover:text-destructive"
                        aria-label={`Eliminar definitivamente ${v.patente}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setAPurgar(v);
                          setConfirmPurgarTexto("");
                        }}
                      >
                        <Icon name="delete_forever" size={16} />
                      </Button>
                    </Tip>
                  </div>
                ) : (
                  <div className="flex items-center justify-end gap-0.5">
                    <Tip texto="Abrir ficha">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Abrir ficha de ${v.patente}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/vehiculos/${v.id}/ficha`);
                        }}
                      >
                        <Icon name="chevron_right" size={17} />
                      </Button>
                    </Tip>
                    <Tip texto="Mover a la papelera">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-soft hover:text-destructive"
                        aria-label={`Mover ${v.patente} a la papelera`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setAEliminar(v);
                        }}
                      >
                        {/* `delete`, no `delete_outline`: en Material Symbols el
                            relleno es un EJE (FILL), no un nombre distinto, y la
                            app carga la fuente con FILL@0 — así que `delete` ya
                            es la variante de contorno del prototipo. Un nombre
                            inexistente se dibujaría como su propio texto. */}
                        <Icon name="delete" size={16} />
                      </Button>
                    </Tip>
                  </div>
                ),
            },
          ]}
        />
      )}

      {/* Confirmar: mover a papelera */}
      <AlertDialog open={aEliminar !== null} onOpenChange={(open) => !open && setAEliminar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mover a papelera</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                {aEliminar ? (
                  <div className="space-y-2">
                    <p>
                      ¿Quieres mover el vehículo <strong>{aEliminar.patente}</strong> a la papelera?
                    </p>
                    <p>
                      Va a desaparecer del inventario pero puedes{" "}
                      <strong>restaurarlo en cualquier momento</strong> desde la pestaña Papelera.
                    </p>
                    <p className="text-muted-foreground text-xs">
                      Si sólo quieres sacarlo de las asignaciones operativas sin quitarlo de la
                      empresa, desactívalo desde su ficha.
                    </p>
                  </div>
                ) : null}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={eliminando}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                moverAPapelera();
              }}
              disabled={eliminando}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {eliminando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Mover a papelera"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmar: restaurar */}
      <AlertDialog open={aRestaurar !== null} onOpenChange={(open) => !open && setARestaurar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restaurar vehículo</AlertDialogTitle>
            <AlertDialogDescription>
              {aRestaurar ? (
                <>
                  ¿Restaurar el vehículo <strong>{aRestaurar.patente}</strong>? Vuelve al inventario
                  con el estado que tenía antes de ser eliminado.
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={restaurando}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                restaurar();
              }}
              disabled={restaurando}
            >
              {restaurando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Restaurar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmar: purgar (typed-confirm) */}
      <AlertDialog
        open={aPurgar !== null}
        onOpenChange={(open) => {
          if (!open) {
            setAPurgar(null);
            setConfirmPurgarTexto("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">Eliminar permanentemente</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                {aPurgar ? (
                  <div className="space-y-3">
                    <p>
                      Estás por eliminar definitivamente el vehículo{" "}
                      <strong>{aPurgar.patente}</strong>. Esta acción es{" "}
                      <strong>irreversible</strong>: se borrarán todos sus datos del sistema,
                      incluidos sus documentos.
                    </p>
                    <p className="font-medium">
                      Escribe <span className="fc-num text-destructive">ELIMINAR</span> para
                      confirmar:
                    </p>
                    <Input
                      value={confirmPurgarTexto}
                      onChange={(e) => setConfirmPurgarTexto(e.target.value)}
                      placeholder="ELIMINAR"
                      className="fc-num"
                      disabled={purgando}
                      autoFocus
                    />
                  </div>
                ) : null}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={purgando}
              onClick={() => {
                setAPurgar(null);
                setConfirmPurgarTexto("");
              }}
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                purgar();
              }}
              disabled={purgando || confirmPurgarTexto !== "ELIMINAR"}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {purgando ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Eliminar permanentemente"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface VehiculoFormDrawerProps {
  mode: "create" | "edit";
  vehiculo?: Vehiculo;
  onSaved: () => void;
}

/**
 * Envoltorio del drawer de alta — el cuerpo de campos vive en `VehiculoForm`
 * (compartido con el tab Información de la Ficha del vehículo). La EDICIÓN de
 * un vehículo existente vive sólo en la ficha: cambiar marca, áreas o el estado
 * activo sin ver antes sus documentos es justo la decisión que conviene tomar
 * con el contexto delante.
 */
function VehiculoFormDrawer({ mode, vehiculo, onSaved }: VehiculoFormDrawerProps) {
  return (
    <FormDrawerContent>
      <FormDrawerHeader>
        <div className="flex items-center gap-3">
          <span className="bg-primary-50 text-primary-hover flex size-9 shrink-0 items-center justify-center rounded-[10px]">
            <Icon name="local_shipping" size={20} />
          </span>
          <div className="min-w-0 flex-1">
            <FormDrawerTitle>
              {mode === "create" ? "Nuevo vehículo" : "Editar vehículo"}
            </FormDrawerTitle>
            <FormDrawerDescription>
              {mode === "create"
                ? "Queda disponible para los checklists de terreno y las asignaciones."
                : "Actualiza los datos del vehículo."}
            </FormDrawerDescription>
          </div>
        </div>
      </FormDrawerHeader>

      <VehiculoForm
        mode={mode}
        vehiculo={vehiculo}
        onSaved={onSaved}
        formClassName={formDrawerBodyClass}
        renderFooter={({ isSubmitting, resumen }) => (
          <FormDrawerFooter>
            {/* La pista dice qué falta ANTES de intentar guardar, en vez de
                dejar el botón inerte sin explicar por qué. */}
            <FormDrawerFooterHint>{resumen}</FormDrawerFooterHint>
            <FormDrawerClose asChild>
              <Button type="button" variant="outline" disabled={isSubmitting}>
                Cancelar
              </Button>
            </FormDrawerClose>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="gap-2"
              data-tour="vehiculos-guardar"
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              {mode === "create" ? "Agregar a la flota" : "Guardar cambios"}
            </Button>
          </FormDrawerFooter>
        )}
      />
    </FormDrawerContent>
  );
}
